<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import CpfMatchAPI from 'dashboard/api/fandata/cpfMatch';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';

// FanData (T-12 Fase 3): banner dispensável — ao abrir a conversa, avisa se o mesmo CPF está
// em outro contato/canal (com atendimento ativo) e oferece unificar (merge nativo) + ir para a
// conversa paralela. Dispara a nota cross-canal (Fase 2, idempotente) na detecção.
const props = defineProps({
  chat: { type: Object, required: true },
});

const { t } = useI18n();
const route = useRoute();

const match = ref(null);
const dismissed = ref({});

const displayId = computed(() => props.chat?.id); // no store da conversa, chat.id = display_id
const contact = computed(() => props.chat?.meta?.sender || {});
// T-58: entre as conversas ativas, a que tem ATENDENTE humano é a que muda a conduta —
// o agente precisa se alinhar com uma pessoa, não descobrir que existe outro ticket.
const ativas = computed(() => match.value?.conversas_ativas || []);
const ativa = computed(
  () => ativas.value.find(c => c.atendente) || ativas.value[0] || null
);
const outroAtendente = computed(() => ativa.value?.atendente || null);
// Por qual identificador o sistema casou (CPF, e-mail ou telefone). Muda a confiança do
// agente no aviso: CPF é único por pessoa, telefone pode ser da família.
const motivo = computed(() => match.value?.casou_por_label || null);

const visible = computed(
  () =>
    !!match.value &&
    !dismissed.value[displayId.value] &&
    (match.value.duplicados?.length || match.value.conversas_ativas?.length)
);

const fetchMatch = async () => {
  match.value = null;
  if (!displayId.value) return;
  try {
    const { data } = await CpfMatchAPI.get(displayId.value);
    const temMatch =
      data?.duplicados?.length || data?.conversas_ativas?.length;
    match.value = temMatch ? data : null;
    if (temMatch) CpfMatchAPI.notar(displayId.value).catch(() => {});
  } catch (e) {
    match.value = null;
  }
};

const dismiss = () => {
  dismissed.value = { ...dismissed.value, [displayId.value]: true };
};

// Nova guia: quem abre isto está no meio de um atendimento e precisa VER a outra conversa,
// não trocar de contexto e perder o que estava fazendo.
const irParaAtiva = () => {
  if (!ativa.value) return;
  const url = frontendURL(
    conversationUrl({ accountId: route.params.accountId, id: ativa.value.id })
  );
  window.open(url, '_blank', 'noopener');
};

onMounted(fetchMatch);
watch(displayId, fetchMatch);
</script>

<template>
  <div
    v-if="visible"
    class="flex items-center gap-2 px-4 py-2.5 text-sm border-b border-l-4 bg-n-amber-4 border-n-weak border-l-n-amber-9"
  >
    <span
      class="flex-shrink-0 i-lucide-user-round-search text-n-amber-11 size-[18px]"
    />
    <div class="flex-1 min-w-0 text-n-slate-12">
      <span v-if="outroAtendente" class="font-medium">
        {{
          motivo
            ? t('FANDATA_CPF.SIMULTANEO_AGENTE_MOTIVO', {
                nome: ativa.contato,
                agente: outroAtendente.nome,
                canal: ativa.canal,
                motivo,
              })
            : t('FANDATA_CPF.SIMULTANEO_AGENTE', {
                nome: ativa.contato,
                agente: outroAtendente.nome,
                canal: ativa.canal,
              })
        }}
      </span>
      <span v-else-if="ativa">
        {{
          t('FANDATA_CPF.SIMULTANEO', {
            nome: ativa.contato,
            canal: ativa.canal,
          })
        }}
      </span>
      <span v-else>
        {{
          motivo
            ? t('FANDATA_CPF.DUPLICADO_MOTIVO', { motivo })
            : t('FANDATA_CPF.DUPLICADO')
        }}
      </span>
    </div>
    <!-- T-116: o número do ticket vira o próprio link. A Mari pediu para conseguir pular
         direto para a outra conversa e ler o que já foi dito; um botão "Ir" genérico não
         dizia para onde se estava indo. -->
    <button
      v-if="ativa"
      type="button"
      class="flex-shrink-0 font-semibold underline text-n-amber-11 hover:text-n-amber-12"
      @click="irParaAtiva"
    >
      {{ t('FANDATA_CPF.ABRIR_TICKET', { ticket: ativa.ticket }) }}
    </button>
    <ContactMergeModal :primary-contact="contact">
      <template #trigger>
        <Button :label="t('FANDATA_CPF.UNIFICAR')" faded slate size="xs" />
      </template>
    </ContactMergeModal>
    <Button
      icon="i-lucide-x"
      variant="ghost"
      slate
      size="xs"
      @click="dismiss"
    />
  </div>
</template>
